import React, { useState } from 'react';
import { FaSave, FaTimes, FaCheckCircle, FaFilePdf, FaUserShield, FaBriefcase, FaUsers, FaLock } from 'react-icons/fa';

const NewJerseyChecklistForm = ({ initialData, onSave, onCancel, isSubmitting }) => {
  const defaultData = {
    raName: '',
    raNumber: '',
    raAddr1: '',
    raAddr2: '',
    raCityStateZip: '',
    businessAddressChange: '',
    directors: [
      { name: '', title: '', address: '' },
      { name: '', title: '', address: '' }
    ],
    workersCompRequired: '',
    authName: '',
    authTitle: '',
    authPhone: '',
    authAddress: ''
  };

  const [formData, setFormData] = useState({
    ...defaultData,
    ...(initialData || {})
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDirectorChange = (index, field, value) => {
    const newDirectors = [...formData.directors];
    newDirectors[index][field] = value;
    setFormData(prev => ({ ...prev, directors: newDirectors }));
  };

  const addDirector = () => {
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, { name: '', title: '', address: '' }]
    }));
  };

  const removeDirector = (index) => {
    if (formData.directors.length <= 1) return;
    const newDirectors = formData.directors.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, directors: newDirectors }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, true);
  };

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-dark-700">
      <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center bg-gradient-to-r from-indigo-600 to-violet-600">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FaCheckCircle /> New Jersey Annual Report Checklist
        </h2>
        <button onClick={onCancel} className="text-white/80 hover:text-white transition-colors">
          <FaTimes className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-10">
        {/* Section A: Registered Agent */}
        <div className="space-y-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-sm font-bold">A</span>
            Registered Agent and Office
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Registered Agent Name</label>
              <input type="text" name="raName" value={formData.raName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Registered Agent #</label>
              <input type="text" name="raNumber" value={formData.raNumber} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 outline-none" required />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Address Line 1</label>
              <input type="text" name="raAddr1" value={formData.raAddr1} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 outline-none" required />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Address Line 2 (Optional)</label>
              <input type="text" name="raAddr2" value={formData.raAddr2} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 outline-none" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">City, State, Zip</label>
              <input type="text" name="raCityStateZip" value={formData.raCityStateZip} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 outline-none" placeholder="e.g. Trenton, NJ 08608" required />
            </div>
          </div>
        </div>

        {/* Section B: Business Address Change */}
        <div className="space-y-4 pt-8 border-t border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center text-sm font-bold">B</span>
            Business Address Change
          </h3>
          <div className="space-y-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">If there is any change in your business address, please specify here:</label>
            <textarea
              name="businessAddressChange"
              value={formData.businessAddressChange}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none"
              placeholder="Leave blank if no changes"
            />
          </div>
        </div>

        {/* Section C: Directors */}
        <div className="space-y-6 pt-8 border-t border-gray-100 dark:border-dark-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-sm font-bold">C</span>
              List of Directors
            </h3>
            <button 
              type="button" 
              onClick={addDirector}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-2"
            >
              <FaTimes className="rotate-45" /> Add Director
            </button>
          </div>
          <div className="space-y-6">
            {formData.directors.map((director, index) => (
              <div key={index} className="p-5 rounded-2xl bg-gray-50/50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-700 relative group">
                {formData.directors.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => removeDirector(index)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <FaTimes />
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Name</label>
                    <input type="text" value={director.name} onChange={(e) => handleDirectorChange(index, 'name', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none text-sm" placeholder="Full Name" required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Title</label>
                    <input type="text" value={director.title} onChange={(e) => handleDirectorChange(index, 'title', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none text-sm" placeholder="e.g. Director, President" required />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Address</label>
                    <input type="text" value={director.address} onChange={(e) => handleDirectorChange(index, 'address', e.target.value)} className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none text-sm" placeholder="Full Address" required />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section D: Workers Comp */}
        <div className="space-y-4 pt-8 border-t border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-sm font-bold">D</span>
            Workers' Compensation
          </h3>
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-semibold">Is this business entity required to provide worker's compensation insurance?</p>
            <div className="flex gap-6">
              {['Yes', 'No'].map(option => (
                <label key={option} className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="radio"
                      name="workersCompRequired"
                      value={option}
                      checked={formData.workersCompRequired === option}
                      onChange={handleChange}
                      className="peer sr-only"
                    />
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-dark-600 peer-checked:border-emerald-500 transition-all"></div>
                    <div className="absolute inset-1 rounded-full bg-emerald-500 scale-0 peer-checked:scale-100 transition-transform"></div>
                  </div>
                  <span className="text-sm font-bold text-gray-600 dark:text-gray-400 group-hover:text-emerald-500 transition-colors">{option}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Section E: Authorization */}
        <div className="space-y-6 pt-8 border-t border-gray-100 dark:border-dark-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-sm font-bold">E</span>
            Authorization
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Name</label>
              <input type="text" name="authName" value={formData.authName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Title</label>
              <input type="text" name="authTitle" value={formData.authTitle} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Phone Number</label>
              <input type="text" name="authPhone" value={formData.authPhone} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Address</label>
              <input type="text" name="authAddress" value={formData.authAddress} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none" required />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 pt-10 border-t border-gray-100 dark:border-dark-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-8 py-3 rounded-xl font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 transition-all text-center"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(formData, false)}
            disabled={isSubmitting}
            className="px-8 py-3 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <FaSave /> Save as Draft
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-10 py-3 bg-gradient-to-r from-red-600 to-amber-600 text-white font-bold rounded-xl shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/40 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> Submitting...</>
            ) : (
              <><FaFilePdf /> Submit NJ Checklist</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewJerseyChecklistForm;
